import { byIdsResultJSONZ, ByIdsResultJSON } from "_shared/_api/by-ids/by-ids-result";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { listCountsHook, listCountsTags } from "_shared/_api/list-counts/list-counts-hook";
import { api } from "_shared/_stores/api";
import { calendarEventListTag } from "calendar/_api/calendar-event-api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Session from "users/__session/session";
import { serializeQueryArgsWithoutSession } from "users/__session/session-query-cache-keys";
import { UpdateProposalAPIRequest } from "work/proposals/_api/commands/update-proposal-api-request";
import WorkProposalAPIRequest from "work/proposals/_api/commands/work-proposal-api-request";
import ProposalQuery from "work/proposals/_api/queries/proposal-query";
import {
  ProposalListCountsJSONZ,
  type ProposalListCounts,
} from "work/proposals/_api/queries/_schemas/proposal-list-counts-schema";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  DraftDeletionResultJSON,
  DraftDeletionResultJSONZ,
} from "work/proposals/draft-deletion/draft-deletion-result";
import Proposal from "work/proposals/proposal";
import { workAgreementVersionHeader } from "work/work-agreements/_api/work-agreement-version-header";
import VendorTeamDepartureSchema, {
  type VendorTeamDepartureJSON,
} from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-departure-schema";
import z from "zod";

export const proposalsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getProposals: build.query<Paginated<ProposalJSON>, { query: ProposalQuery; interfaceName: string }>({
      query: ({ query }) => ({
        url: "work/proposals",
        params: query?.searchParams,
      }),
      rawResponseSchema: z.array(ProposalSchema.ProposalJSONZ),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const { data: proposalsResponse } = await queryFulfilled;
          const session = Session.loadFromStorage(undefined as never);
          proposalsResponse.data.forEach((proposalJSON) => {
            if (!proposalJSON.id) return;
            dispatch(
              proposalsApi.util.upsertQueryData(
                "getProposalById",
                { proposalId: proposalJSON.id, session },
                proposalJSON,
              ),
            );
          });
        } catch {
          // Failed list requests have no rows to hydrate into the per-proposal cache.
        }
      },
      transformResponse: (response: ProposalJSON[], meta): Paginated<ProposalJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
              // Tag each item
              ...result.data.map((item) => ({
                type: "Proposal" as const,
                id: item.id ?? undefined,
              })),
              // Tag the list itself
              { type: "Proposal" as const, id: "LIST" },
            ]
          : [{ type: "Proposal" as const, id: "LIST" }],
    }),
    getProposalById: build.query<ProposalJSON | null, { proposalId: string; session: Session }>({
      query: ({ proposalId }) => `work/proposals/${proposalId}`,
      serializeQueryArgs: serializeQueryArgsWithoutSession,
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, error, request) => [{ type: "Proposal", id: request.proposalId }],
    }),
    /**
     * Who has left the vendor team of the work this proposal replaces (#6096). Authorized by the
     * proposal, so every reader of a change order gets them, a reviewer carried forward onto it
     * included. Empty for a proposal that replaces no work.
     *
     * The answer is the replaced work's, so it is tagged with that work as well as the proposal. A
     * roster change on the live work invalidates the work's tag -- the re-staffing command and the
     * realtime `work-agreement-updated` both do -- so somebody put back on the team stops reading as
     * departed on an open change order at once, rather than at the next reload.
     */
    getReplacedWorkDepartures: build.query<
      VendorTeamDepartureJSON[],
      { proposalId: string; replacesWorkAgreementId: string; session: Session }
    >({
      query: ({ proposalId }) => `work/proposals/${proposalId}/replaced-work-departures`,
      serializeQueryArgs: serializeQueryArgsWithoutSession,
      rawResponseSchema: z.array(VendorTeamDepartureSchema.VendorTeamDepartureJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, error, request) => [
        { type: "Proposal", id: request.proposalId },
        { type: "WorkAgreement", id: request.replacesWorkAgreementId },
      ],
    }),
    /**
     * The proposals the reader has left, each as it was when they left it (#5833). Kept views, not
     * rows of the proposal list: the negotiation goes on without them.
     */
    getProposalsTheReaderLeft: build.query<ProposalJSON[], { session: Session }>({
      query: () => "work/proposals/left",
      serializeQueryArgs: serializeQueryArgsWithoutSession,
      rawResponseSchema: z.array(ProposalSchema.ProposalJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Proposal" as const, id: "LEFT" }],
    }),
    // Every id asked about is answered: a proposal in `found`, or an `unavailable` entry saying
    // whether it was refused or names nothing (#6122).
    getProposalsByIds: build.query<ByIdsResultJSON<BarebonesProposalResponse>, { proposalIds: string[] }>({
      query: ({ proposalIds }) => ({
        url: "work/proposals/get-by-ids",
        body: proposalIds,
        method: "POST",
      }),
      rawResponseSchema: byIdsResultJSONZ(z.looseObject({ id: z.string() })),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
              // Tag each item
              ...result.found.map((item) => ({
                type: "Proposal" as const,
                id: item.id ?? undefined,
              })),
              // Tag the list itself
              { type: "Proposal" as const, id: "LIST" },
            ]
          : [{ type: "Proposal" as const, id: "LIST" }],
    }),
    // #endregion

    // #region Counts
    /**
     * Every badge and chip count of the proposal list, in one request (#6709): the Proposals tab's
     * open proposals, and each stage, move and outcome chip as the list's criteria and the other
     * chip groups narrow it.
     *
     * `criteria` is the list's own filter as a query string (`ProposalFilterParameters.searchParams`),
     * so the counts answer the list on screen, its column filters and search included. It provides
     * the list's tag, so whatever refetches the list refetches its counts, and the count tags the
     * mutations already name.
     */
    getProposalListCounts: build.query<ProposalListCounts, { criteria: string }>({
      query: ({ criteria }) => `work/proposals/counts${criteria ? `?${criteria}` : ""}`,
      rawResponseSchema: ProposalListCountsJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: listCountsTags("Proposal", "DraftCount", "ActiveCount", "ArchiveCount", "InboxCount"),
    }),
    // #endregion

    // #region Create
    createProposal: build.mutation<
      ProposalJSON,
      { proposal: Proposal; session: Session; commandId: string }
    >({
      query: ({ proposal, session, commandId }) => {
        const request = new WorkProposalAPIRequest(proposal, session, undefined, commandId);
        return {
          url: "work/proposals",
          method: "POST",
          body: request.payload,
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
      ],
    }),
    // #endregion

    // #region Share
    saveProposal: build.mutation<
      ProposalJSON,
      { originalProposal: Proposal; updatedProposal: Proposal; session: Session }
    >({
      query: ({ originalProposal, updatedProposal: proposal, session }) => {
        const request = new UpdateProposalAPIRequest({ originalProposal, proposal, session });
        if (request.payload.length === 0) {
          throw new Error("No changes to save");
        }
        return {
          url: `work/proposals/${proposal.id?.value}`,
          method: "PATCH",
          body: request.payload,
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Delete
    deleteProposal: build.mutation<void, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Proposal", id: id.proposalId },
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Delete Drafts
    // 200 when every draft was deleted and 207 when some were not, each with its reason; 403 when the
    // reader created none of them (#6577).
    bulkDeleteProposals: build.mutation<DraftDeletionResultJSON, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `work/draft-proposals`,
        method: "DELETE",
        body: ids,
      }),
      rawResponseSchema: DraftDeletionResultJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [
        ...args.ids.map((id) => ({ type: "Proposal" as const, id })),
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
      ],
    }),
    // #endregion

    // #region Cancel
    cancelProposal: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/cancel`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "InboxCount" },
        { type: "Proposal", id: "ArchiveCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Hire
    hireProposal: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/hire`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "InboxCount" },
        { type: "Proposal", id: "ArchiveCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Submit
    submitProposal: build.mutation<ProposalJSON, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/submit`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "InboxCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Approve
    approveProposal: build.mutation<ProposalJSON, { proposalId: string; session: Session }>({
      query: ({ proposalId }) => {
        if (!proposalId) {
          throw new Error("Proposal ID is required for approval");
        }
        return {
          url: `work/proposals/${proposalId}/approve`,
          method: "POST",
          body: {},
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: approvedProposal } = await queryFulfilled;
          dispatch(proposalsApi.util.upsertQueryData("getProposalById", arg, approvedProposal));
        } catch {
          // Failed approval requests have no proposal data to hydrate into the per-proposal cache.
        }
      },
      invalidatesTags: [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "InboxCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Reject
    rejectProposal: build.mutation<ProposalJSON, { proposalId?: string }>({
      query: ({ proposalId }) => {
        if (!proposalId) {
          throw new Error("Proposal ID is required for rejection");
        }
        return {
          url: `work/proposals/${proposalId}/reject`,
          method: "POST",
          body: {},
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "InboxCount" },
        { type: "Proposal", id: "ArchiveCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Revision
    requestProposalRevision: build.mutation<ProposalJSON, { proposal: Proposal; session: Session }>({
      query: ({ proposal, session }) => {
        if (!proposal.redlinedRevision) {
          throw new Error("No redlined revision found on proposal");
        }
        const request = new WorkProposalAPIRequest(proposal.redlinedRevision, session);
        return {
          url: `work/proposals/${proposal.id?.value}/request-revision`,
          method: "POST",
          body: request.payload,
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "InboxCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Give Reviewer Approval
    giveReviewerApproval: build.mutation<ProposalJSON | null, { proposalId: string }>({
      query: ({ proposalId }) => ({
        url: `work/proposals/${proposalId}/reviewer-approve`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "InboxCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Quit Team
    quitProposalTeam: build.mutation<ProposalJSON | null, { workAgreementId: string; version: number }>({
      query: ({ workAgreementId, version }) => ({
        url: `work/${workAgreementId}/teams/quit`,
        method: "POST",
        body: {},
        headers: workAgreementVersionHeader(version),
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "InboxCount" },
        { type: "Proposal", id: "ArchiveCount" },
        calendarEventListTag,
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetProposalsQuery,
  useGetProposalByIdQuery,
  useGetReplacedWorkDeparturesQuery,
  useGetProposalsTheReaderLeftQuery,
  useGetProposalsByIdsQuery,
  useGetProposalListCountsQuery,
  useCreateProposalMutation,
  useSaveProposalMutation,
  useDeleteProposalMutation,
  useBulkDeleteProposalsMutation,
  useCancelProposalMutation,
  useHireProposalMutation,
  useSubmitProposalMutation,
  useApproveProposalMutation,
  useRejectProposalMutation,
  useRequestProposalRevisionMutation,
  useGiveReviewerApprovalMutation,
  useQuitProposalTeamMutation,
} = proposalsApi;

/** Every badge and chip count of the proposal list, from its one counts request (#6709). */
export const useProposalListCounts = listCountsHook<{ criteria: string }, ProposalListCounts>(
  useGetProposalListCountsQuery,
);
